import { vehicleString } from '../../utilities';
import {
  getTranslationKey,
  humanizeEnumBasic,
  keys,
} from '../../utilities/translator/translation_keys';
import i18n from '../../i18n';
import { groupTypes } from '../../utilities/reports';
import { getLocalTime } from '../../utilities/time';
import { getLabel as getIncidentStatus } from '../../utilities/incidents';

export const formatters = {
  workspaceId: (id, workspaces) => {
    if (!id) return 'Company (Shared)';
    const match = workspaces?.find((w) => String(w.id) === String(id));
    return match?.title ?? String(id);
  },
  templateId: (id, templates) => {
    const match = templates?.find((w) => String(w.id) === String(id));
    return match?.title ?? String(id);
  },
  vehicleId: (id, vehicles) => {
    const match = vehicles?.find((v) => String(v.id) === String(id));
    return match ? vehicleString(match) : String(id);
  },
  dueDate: (v) => (v ? new Date(Number(v)).toLocaleDateString() : ''),
  creatorId: (id, users) => {
    if (!id) return 'Anonymous';
    const match = users?.find((u) => String(u.id) === String(id));
    return match
      ? `${match.firstName ?? ''} ${match.lastName ?? ''}`.trim()
      : String(id);
  },
  assigneeId: (id, users) => {
    if (!id) return 'None';
    const match = users?.find((u) => String(u.id) === String(id));
    return match
      ? `${match.firstName ?? ''} ${match.lastName ?? ''}`.trim()
      : String(id);
  },
  labels: (v) => {
    if (v?.length < 1) return 'None';

    return typeof v === 'string' ? v : v?.map((l) => l?.name ?? '')?.join(', ');
  },
  isDraft: (v) =>
    v === 'false'
      ? i18n.t(keys.common.COMPLETE)
      : i18n.t(keys.common.IN_PROGRESS),
  status: (v, parentType) => {
    switch (parentType) {
      case 'incident':
        return getIncidentStatus(v);
      default:
        return v;
    }
  },
  lostTime: () => i18n.t(keys.incidents.LOST_TIME),
};

export function FormatTypes(key, lookups, parentType) {
  const f = formatters[key];
  if (!f) {
    return (v) => (v == null ? '' : v);
  }
  const {
    allWorkspaces: workspaces,
    allUsers: users,
    allVehicles: vehicles,
    allTemplates: templates,
    allLabels: labels,
  } = lookups || {};
  return (v) => {
    switch (key) {
      case 'workspaceId':
      case 'workspace':
        return f(v, workspaces);
      case 'templateId':
      case 'template':
        return f(v, templates);
      case 'vehicleId':
      case 'vehicle':
        return f(v, vehicles);
      case 'creatorId':
      case 'creator':
        return f(v, users);
      case 'assigneeId':
      case 'assignee':
        return f(v, users);
      case 'dueDate':
        return f(v);
      case 'labels':
        return f(v, labels);
      case 'isDraft':
        return f(v);
      case 'status':
        return f(v, parentType);
      case 'lostTime':
        return f(v);
      default:
        return (v) =>
          v == null
            ? ''
            : i18n.t(getTranslationKey(String(v))) ||
              i18n.t(keys.common[String(v)]) ||
              humanizeEnumBasic(v) ||
              String(v);
    }
  };
}
const showDescription = [
  'incidents',
  'actionItems',
  'observations',
  'correctiveActions',
];

export function groupCountsForPdf({
  table,
  group,
  subgroup = null,
  model,
  timeSetting = 'standard',
} = {}) {
  const isMilitary = timeSetting === 'military';
  const timeFormat = isMilitary
    ? 'dddd, MMMM Do, YYYY HH:mm'
    : 'dddd, MMMM Do, YYYY h:mma';
  const key = model === 'correctiveActions' ? 'narrative' : 'description';
  const columns = [
    {
      id: group,
      accessorKey: group,
      header: groupTypes?.[group]?.title || group,
    },
    ...(subgroup
      ? [
          {
            id: subgroup,
            accessorKey: subgroup,
            header: groupTypes?.[subgroup]?.title || subgroup,
          },
        ]
      : []),
    ...(showDescription.includes(model)
      ? [
          {
            id: key,
            accessorKey: key,
            header: '',
          },
        ]
      : []),

    { id: '__count', accessorKey: '__count', header: 'Count' },
  ];

  const rows = [];
  const flat = table.getExpandedRowModel().flatRows ?? [];
  const hasSubgroups = Boolean(subgroup) && flat.some((r) => r.depth === 1);
  const groupMap = new Map();
  const groups = [];

  flat.forEach((r) => {
    if (r.depth === 0) {
      const g = {
        id: r.id,
        value: r.getValue(group),
        leafs: r.getLeafRows?.() ?? [],
        subgroups: [],
        rowRef: r,
      };
      groups.push(g);
      groupMap.set(r.id, g);
    } else if (hasSubgroups && r.depth === 1) {
      const parent = r.getParentRow?.();
      const g = parent ? groupMap.get(parent.id) : groups[groups.length - 1];
      if (g) {
        g.subgroups.push({
          id: r.id,
          value: r.getValue(subgroup),
          leafs: r.getLeafRows?.() ?? [],
          rowRef: r,
        });
      }
    }
  });
  groups.forEach((g) => {
    rows.push({
      rowType: 'group',
      id: g.id,
      [group]: g.value,
      __count: hasSubgroups ? undefined : g.leafs.length,
    });

    if (hasSubgroups) {
      g.subgroups.forEach((sg) => {
        rows.push({
          rowType: 'subgroup',
          id: sg.id,
          [group]: g.value,
          [subgroup]: sg.value,
          __count: sg.leafs.length,
        });
        sg.leafs.forEach((l, idx) => {
          const original = l?.original ?? {};
          const description = `${getLocalTime(
            model === 'incidents'
              ? original?.dateOfIncident
              : original.dateCreated,
          ).format(timeFormat)} - ${original.description || 'No Description'}`;
          rows.push({
            rowType: 'child',
            id: l?.id ?? `${sg.id}__${idx}`,
            [group]: '',
            ...(subgroup ? { [subgroup]: '' } : {}),
            __count: undefined,
            ...(showDescription.includes(model) ? { description } : {}),
          });
        });
      });
    } else {
      g.leafs.forEach((l, idx) => {
        const original = l?.original ?? {};
        const description = `${getLocalTime(
          model === 'incidents'
            ? original?.dateOfIncident
            : original.dateCreated,
        ).format(timeFormat)} - ${original.description || 'No Description'}`;
        rows.push({
          rowType: 'child',
          id: l?.id ?? `${g.id}__${idx}`,
          [group]: '',
          __count: undefined,
          ...(showDescription.includes(model)
            ? {
                description,
              }
            : {}),
        });
      });
    }
  });

  return { columns, rows };
}
